import Fuse from 'fuse.js';
import { useEffect, useMemo, useState } from 'react';
import { PRICE_RANGE, INDEPENDENT_BRAND, computeDynamicPriceMax } from '../constants';
import { useTranslation } from '@shared/core';
import { AccommodationCategory } from '@graphql/generated-base';
import type { AccommodationListingDTO, GuestRating } from '@shared/types/Accommodations.types';
import { useFilterQueryParams } from './useAccommodationsConsoleSearchParamsState/useFilterQueryParams';
import { useListingsSortQueryParam } from './useAccommodationsConsoleSearchParamsState/useListingsSortQueryParam';
import { useListingsPanelTelemetry } from '../components/ListingsPanel/ListingsPanel.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { SortOption } from '@shared/constants/accommodations';

export const useListingFilters = (properties: ReadonlyArray<AccommodationListingDTO>, isFetchingListingPrices: boolean, defaultSortOption: SortOption) => {
  const { t } = useTranslation('accommodationsConsole');
  const filterPanelTrans = t('filterPanel');
  const { currentFilters, hasActiveFilters, updateFilters, resetFilters } = useFilterQueryParams();
  const { currentSort } = useListingsSortQueryParam(defaultSortOption);
  const [propertyNameSearch, setPropertyNameSearchState] = useState('');

  const telemetry = useListingsPanelTelemetry();

  const supportedFacilities = useMemo(
    () => [
      { label: filterPanelTrans.facilities.parking() },
      { label: filterPanelTrans.facilities.restaurant() },
      { label: filterPanelTrans.facilities.internet() },
      { label: filterPanelTrans.facilities.nonSmoking() },
      { label: filterPanelTrans.facilities.spa() },
      { label: filterPanelTrans.facilities.bar() },
      { label: filterPanelTrans.facilities.breakfast() },
      { label: filterPanelTrans.facilities.atm() },
      { label: filterPanelTrans.facilities.swimmingPool() },
      { label: filterPanelTrans.facilities.airConditioning() },
      { label: filterPanelTrans.facilities.petsAllowed() },
    ],
    [filterPanelTrans],
  );

  const supportedBrands = useMemo(() => {
    const brandSet = new Set<string>();
    let hasIndependent = false;
    for (const property of properties) {
      if (property.chain) {
        brandSet.add(property.chain);
      } else {
        hasIndependent = true;
      }
    }
    const sorted = Array.from(brandSet)
      .sort((a, b) => a.localeCompare(b))
      .map(brand => ({ label: brand }));
    if (hasIndependent) {
      sorted.unshift({ label: INDEPENDENT_BRAND });
    }
    return sorted;
  }, [properties]);

  const listingPrices = useMemo(
    () => properties.map(p => p.pricingInfo?.values?.nightlyBaseRate).filter((price): price is number => price != null && Number.isFinite(price) && price >= 0),
    [properties],
  );

  const dynamicPriceMax = useMemo(() => computeDynamicPriceMax(listingPrices), [listingPrices]);

  const fuse = useMemo(
    () =>
      new Fuse(properties, {
        keys: ['name'],
        threshold: 0.3,
      }),
    [properties],
  );

  const searchFilteredProperties = useMemo(() => {
    if (!propertyNameSearch) {
      return properties;
    }
    return fuse.search(propertyNameSearch);
  }, [fuse, propertyNameSearch, properties]);

  useEffect(() => {
    if (propertyNameSearch) {
      telemetry.propertyNameSearched({ keyword: propertyNameSearch, resultCount: searchFilteredProperties.length });
    }
  }, [propertyNameSearch, searchFilteredProperties.length, telemetry]);

  const setPropertyNameSearch = useEventCallback((search: string) => {
    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      const shouldResetFilters = hasActiveFilters;
      const shouldResetSort = currentSort !== defaultSortOption;

      if (shouldResetFilters || shouldResetSort) {
        resetFilters(shouldResetSort ? defaultSortOption : undefined);
      }
    }

    setPropertyNameSearchState(trimmedSearch);
  });

  // Apply filters to properties
  const filteredProperties = useMemo(() => {
    let filtered = searchFilteredProperties;

    // Facilities filter
    if (currentFilters.facilities?.length) {
      filtered = filtered.filter(property => {
        const propertyFacilities = property.facilities.map((f: { label: string }) => f.label);
        return currentFilters.facilities!.every(facility => propertyFacilities.includes(facility));
      });
    }

    // Star rating filter
    if (currentFilters.starRating?.length) {
      filtered = filtered.filter(property => property.starRating && currentFilters.starRating!.includes(property.starRating));
    }

    // User rating filter
    if (currentFilters.userRating?.length) {
      filtered = filtered.filter(property => {
        const rating = property.userRating?.ratingValue;
        return rating && currentFilters.userRating!.some((minRating: GuestRating) => rating >= minRating);
      });
    }

    // CUG filter
    if (currentFilters.cug) {
      filtered = filtered.filter(property => {
        const discountPercentage = property.pricingInfo?.values?.discountPercentage;
        return typeof discountPercentage === 'number' && discountPercentage > 0;
      });
    }

    // Price range filter
    if (currentFilters.priceRange && !isFetchingListingPrices) {
      const [min, max] = currentFilters.priceRange;
      // Skip filtering when at default range or at dynamic max (both mean "show all")
      const isAtDefaultRange = min === PRICE_RANGE.MIN && (max >= dynamicPriceMax || max === PRICE_RANGE.MAX);
      if (!isAtDefaultRange) {
        filtered = filtered.filter(property => {
          const nightlyBaseRate = property.pricingInfo?.values?.nightlyBaseRate;
          if (typeof nightlyBaseRate !== 'number') return false;
          const meetsMinPrice = nightlyBaseRate >= min;
          const meetsMaxPrice = max >= dynamicPriceMax ? true : nightlyBaseRate <= max;
          return meetsMinPrice && meetsMaxPrice;
        });
      }
    }

    // Property category filter
    if (currentFilters.propertyCategories?.length) {
      filtered = filtered.filter(property => property.accommodationCategory && currentFilters.propertyCategories.includes(property.accommodationCategory));
    }

    // Free cancellation filter
    if (currentFilters.freeCancellation) {
      filtered = filtered.filter(property => property.hasFreeCancellation === true);
    }

    // Brand filter — null chain maps to INDEPENDENT_BRAND
    if (currentFilters.brands?.length) {
      filtered = filtered.filter(property => {
        const brand = property.chain || INDEPENDENT_BRAND;
        return currentFilters.brands.includes(brand);
      });
    }

    return filtered;
  }, [searchFilteredProperties, currentFilters, isFetchingListingPrices, dynamicPriceMax]);

  // Calculate counts for each rating, facility, and brand considering other active filters
  const { facilityCounts, starRatingCounts, guestRatingCounts, propertyCategoryCounts, brandCounts } = useMemo(() => {
    const selectedFacilities = currentFilters.facilities || [];
    const selectedStarRatings = currentFilters.starRating || [];
    const selectedUserRatings = currentFilters.userRating || [];
    const selectedPropertyCategories = currentFilters.propertyCategories || [];
    const selectedBrands = currentFilters.brands || [];

    const facilityCounts: Record<string, number> = {};
    supportedFacilities.forEach(f => {
      facilityCounts[f.label] = 0;
    });
    const starRatingCounts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const guestRatingCounts: Record<number, number> = { 6: 0, 7: 0, 8: 0, 9: 0 };
    const propertyCategoryCounts: Record<AccommodationCategory, number> = {
      [AccommodationCategory.Hotel]: 0,
      [AccommodationCategory.VacationRental]: 0,
      [AccommodationCategory.Other]: 0,
    };
    const brandCounts: Record<string, number> = {};
    supportedBrands.forEach(b => {
      brandCounts[b.label] = 0;
    });

    for (const property of searchFilteredProperties) {
      const propertyFacilities = property.facilities.map((f: { label: string }) => f.label);
      const hasAllSelectedFacilities = selectedFacilities.every(f => propertyFacilities.includes(f));
      const passesStarRating = !selectedStarRatings.length || (property.starRating && selectedStarRatings.includes(property.starRating));
      const userRating = property.userRating?.ratingValue;
      const passesUserRating = !selectedUserRatings.length || (userRating && selectedUserRatings.some((minRating: GuestRating) => userRating >= minRating));
      const passesPropertyCategory = !selectedPropertyCategories.length || (property.accommodationCategory && selectedPropertyCategories.includes(property.accommodationCategory));
      const propertyBrand = property.chain || INDEPENDENT_BRAND;
      const passesBrand = !selectedBrands.length || selectedBrands.includes(propertyBrand);
      const passesFreeCancellation = !currentFilters.freeCancellation || property.hasFreeCancellation === true;

      // Facility counts: property must pass all other filters except facilities
      if (passesStarRating && passesUserRating && passesPropertyCategory && passesBrand && hasAllSelectedFacilities && passesFreeCancellation) {
        for (const facilityLabel of propertyFacilities) {
          if (Object.prototype.hasOwnProperty.call(facilityCounts, facilityLabel)) {
            facilityCounts[facilityLabel]++;
          }
        }
      }

      // Star rating counts: property must pass all other filters except star rating
      if (passesUserRating && hasAllSelectedFacilities && passesPropertyCategory && passesBrand && passesFreeCancellation && property.starRating) {
        if (Object.prototype.hasOwnProperty.call(starRatingCounts, property.starRating)) {
          starRatingCounts[property.starRating]++;
        }
      }

      // Guest rating counts: property must pass all other filters except user rating
      if (passesStarRating && hasAllSelectedFacilities && passesPropertyCategory && passesBrand && passesFreeCancellation && userRating) {
        for (const threshold of [6, 7, 8, 9] as const) {
          if (userRating >= threshold) {
            guestRatingCounts[threshold]++;
          }
        }
      }

      // Property category counts: property must pass all other filters except property category
      if (passesStarRating && passesUserRating && hasAllSelectedFacilities && passesBrand && passesFreeCancellation && property.accommodationCategory) {
        propertyCategoryCounts[property.accommodationCategory]++;
      }

      // Brand counts: property must pass all other filters except brand
      if (passesStarRating && passesUserRating && hasAllSelectedFacilities && passesPropertyCategory && passesFreeCancellation) {
        if (Object.prototype.hasOwnProperty.call(brandCounts, propertyBrand)) {
          brandCounts[propertyBrand]++;
        }
      }
    }

    return { facilityCounts, starRatingCounts, guestRatingCounts, propertyCategoryCounts, brandCounts };
  }, [supportedFacilities, supportedBrands, currentFilters, searchFilteredProperties]);

  return {
    filters: currentFilters,
    filteredProperties,
    updateFilters,
    supportedFacilities,
    supportedBrands,
    facilityCounts,
    starRatingCounts,
    guestRatingCounts,
    propertyCategoryCounts,
    brandCounts,
    resetFilters,
    propertyNameSearch,
    setPropertyNameSearch,
    dynamicPriceMax,
    listingPrices,
  };
};
